// THE only RNG in the project. All randomness is injected; Math.random is banned in cores.
// Deterministic, seedable, fast.

export type Rng = () => number; // returns [0, 1)

export const FIXED_SEED = 0x9e3779b9;

/** mulberry32 — small, fast, well-distributed 32-bit PRNG. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a hash of a string to a uint32. Stable across runs/platforms. */
export function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Uniform in [lo, hi). */
export function nextRange(rng: Rng, lo: number, hi: number): number {
  return lo + (hi - lo) * rng();
}

/**
 * Standard-normal generator via Box-Muller, caching the paired second value. Each call
 * to `makeGaussian()` returns an independent generator with its own cache, so determinism
 * never depends on shared global state — give each consumer its own instance.
 */
export function makeGaussian(): (rng: Rng) => number {
  let cache: number | null = null;
  return (rng: Rng): number => {
    if (cache !== null) {
      const v = cache;
      cache = null;
      return v;
    }
    let u = 0;
    let v = 0;
    // avoid log(0)
    while (u === 0) u = rng();
    while (v === 0) v = rng();
    const mag = Math.sqrt(-2.0 * Math.log(u));
    cache = mag * Math.sin(2.0 * Math.PI * v);
    return mag * Math.cos(2.0 * Math.PI * v);
  };
}

/** Pick an item by relative weights (weights need not sum to 1; must be >= 0). */
export function weightedPick<T>(rng: Rng, items: readonly T[], weights: readonly number[]): T {
  if (items.length === 0) throw new Error('weightedPick: empty items');
  if (items.length !== weights.length) throw new Error('weightedPick: length mismatch');
  let total = 0;
  for (const w of weights) total += Math.max(0, w);
  if (total <= 0) return items[items.length - 1];
  let r = rng() * total;
  for (let i = 0; i < items.length; i++) {
    r -= Math.max(0, weights[i]);
    if (r < 0) return items[i];
  }
  return items[items.length - 1];
}

/** Exponential inter-arrival time (seconds) for a Poisson process at the given rate (events/sec). */
export function poissonInterval(rng: Rng, ratePerSec: number): number {
  if (ratePerSec <= 0) return Infinity;
  let u = rng();
  while (u === 0) u = rng();
  return -Math.log(u) / ratePerSec;
}
